import { useQuery } from '@tanstack/react-query';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { reportsService, type ReportFilterKind } from '@/shared/services/reports.service';
import { usePlanGate } from '@/shared/hooks/usePlanGate';

/** Prefix of every customers-report query: an outcome changes them all. */
export const REPORTS_KEY = ['reports', 'customers'] as const;

/**
 * Whether the plan opens the customers report: every /reports/customers
 * route is @RequiresPlan('classic'), so Starter is refused. Asking anyway sent
 * a 403 from Overview, Inbox and Analytics on every visit.
 */
function useReportsPlan(): { allowed: boolean; known: boolean } {
  const gate = usePlanGate('classic');
  return { allowed: gate.allowed && !gate.isLoading, known: !gate.isLoading };
}

/**
 * Customers from social for the last `days` (Task 3.3); `enabled: false` while
 * the flag is off, and, without a request, on a plan that lacks the report:
 * every page already shows its no-report state for that answer.
 */
export function useCustomersReport(days: number) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  const plan = useReportsPlan();
  return useQuery({
    // The plan is in the key: an upgrade asks the server instead of reusing the local answer.
    queryKey: [...REPORTS_KEY, wsId, 'report', days, plan.allowed],
    queryFn: (): Promise<Awaited<ReturnType<typeof reportsService.customers>>> =>
      plan.allowed ? reportsService.customers(days) : Promise.resolve({ enabled: false }),
    enabled: !!wsId && plan.known,
    staleTime: 60_000,
    // A refusal (Starter, below Classic) is an answer, not a failure to retry.
    retry: false,
  });
}

/** The conversations behind a KPI tile, for the inbox filter. */
export function useReportConversationKeys(kind: ReportFilterKind | null, days: number) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  const { allowed } = useReportsPlan();
  return useQuery({
    queryKey: [...REPORTS_KEY, wsId, 'keys', kind, days],
    queryFn: () => reportsService.conversationKeys(kind ?? 'conversation', days),
    enabled: !!wsId && kind !== null && allowed,
    staleTime: 60_000,
  });
}

/** What each post brought (the 💬 badges): one request for every post in view. */
export function usePostCustomerCounts(postIds: readonly string[]) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  const ids = [...new Set(postIds)].sort().slice(0, 100);
  const { allowed } = useReportsPlan();
  return useQuery({
    queryKey: [...REPORTS_KEY, wsId, 'posts', ids.join(',')],
    queryFn: () => reportsService.postCounts(ids),
    enabled: !!wsId && ids.length > 0 && allowed,
    staleTime: 5 * 60_000,
  });
}

export function usePostConversations(postId: string | null) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  const { allowed } = useReportsPlan();
  return useQuery({
    queryKey: [...REPORTS_KEY, wsId, 'post-conversations', postId],
    queryFn: () => reportsService.postConversations(postId ?? ''),
    enabled: !!wsId && !!postId && allowed,
    staleTime: 60_000,
  });
}
